import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useLanguage } from './LanguageContext';
import { translate } from '../locales';

export interface NotificationMessage {
  id: string;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface NotificationContextType {
  notifications: NotificationMessage[];
  addNotification: (message?: string, severity?: NotificationMessage['severity']) => void;
  removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationProviderContent: React.FC<NotificationProviderProps> = ({ children }) => {
  const { language } = useLanguage();
  const [notifications, setNotifications] = useState<NotificationMessage[]>([]);
  const t = (key: any) => translate(key, language as 'en' | 'ua');

  const addNotification = (message?: string, severity: NotificationMessage['severity'] = 'info') => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newNotification: NotificationMessage = {
      id,
      message: message || t(`notifications.${severity}`),
      severity,
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after 4 seconds (matching Snackbar autoHideDuration)
    setTimeout(() => {
      removeNotification(id);
    }, 4000);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ notifications, addNotification, removeNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  return (
    <NotificationProviderContent>
      {children}
    </NotificationProviderContent>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
